import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import CancelButton from '../../components/psc/lib/CancelButton'
import SaveButton from '../../components/psc/lib/SaveButton'
import DeleteButton from '../../components/psc/lib/DeleteButton'
import ErrorMessage from '../../components/psc/lib/ErrorMessage'
import useBrowserTabTitle from '../../hooks/useBrowserTabTitle'
import PickDate from '../../components/psc/lib/PickDate'
import dateFormat from 'dateformat'

const OAuthClient = () => {
    let { objID } = useParams()
    const isNew = objID === "new" ? true : false
    objID = parseInt(objID,10)
    const objectUrl = '/v0/oauth_clients' + (isNew ? '' : '/' + objID)
    const navigate = useNavigate()

    const [name, setName] = useState('')
    const [clientId, setClientId] = useState('')
    const [clientSecret, setClientSecret] = useState('')
    const [redirectUris, setRedirectUris] = useState([''])
    const [expirationType, setExpirationType] = useState('12months')
    const [customDate, setCustomDate] = useState('')
    const [submitActive, setSubmitActive] = useState(false)
    const [error, setError] = useState(null)
    const [showSecretModal, setShowSecretModal] = useState(false)
    const [newClientId, setNewClientId] = useState('')
    const [newClientSecret, setNewClientSecret] = useState('')

    useBrowserTabTitle(isNew ? 'New OAuth Client' : (name || 'OAuth Client'))

    useEffect(() => {
        if ( ! isNew) {
            fetchObject()
        }
    }, [isNew])

    const computeExpiresAt = () => {
        const d = new Date()
        if (expirationType === '6months') {
            d.setMonth(d.getMonth() + 6)
        } else if (expirationType === '12months') {
            d.setMonth(d.getMonth() + 12)
        } else if (expirationType === 'custom' && customDate) {
            return new Date(customDate)
        }
        return d
    }

    const fetchObject = () => {
        fetch(objectUrl, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }
                fromBackend(json.result)
            })
            .catch(function(error) {
                console.log(error)
            })
    }

    const fromBackend = (obj) => {
        setName(obj.name || '')
        setClientId(obj.client_id || '')
        setClientSecret(obj.client_secret || '')
        const uris = obj.redirect_uris?.map(uri => uri.redirect_uri) || []
        setRedirectUris(uris.length > 0 ? uris : [''])
        if (obj.expires_at) {
            setCustomDate(obj.expires_at)
        }
    }

    const toBackend = () => {
        const payload = {
            name: name.trim(),
            redirect_uris: redirectUris.filter(uri => uri.trim()).map(uri => ({ redirect_uri: uri.trim() }))
        }
        if (isNew) {
            payload.expires_at = computeExpiresAt()
        }
        return payload
    }

    const validateBeforeSubmit = () => {
        let errors = []
        if ( ! name.trim() ) {
            errors.push('Name is required')
        }
        if (isNew && expirationType === 'custom' && !customDate) {
            errors.push('Expiration date is required')
        }
        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }
        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setError([ props.message ])
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }
        setSubmitActive(true)

        const submit = {
            objType: 'oauth_clients',
            objID: objID,
            data: toBackend(),
        }

        if (isNew) {
            submit.successCallback = function(json) {
                setNewClientId(json.result?.client_id || '')
                setNewClientSecret(json.result?.client_secret || '')
                setShowSecretModal(true)
                setSubmitActive(false)
            }
            submit.navigateOverride = function() {
                // do nothing — modal will handle navigation
            }
        }

        return submit
    }

    const handleCloseSecretModal = () => {
        setShowSecretModal(false)
        navigate('/cadm/oauth_clients')
    }

    const addRedirectUri = () => {
        setRedirectUris([...redirectUris, ''])
    }

    const removeRedirectUri = (index) => {
        const newUris = [...redirectUris]
        newUris.splice(index, 1)
        if (newUris.length === 0) {
            newUris.push('')
        }
        setRedirectUris(newUris)
    }

    const updateRedirectUri = (index, value) => {
        const newUris = [...redirectUris]
        newUris[index] = value
        setRedirectUris(newUris)
    }

    let requiredIndicator = (<label className="form-required-star">&nbsp;*</label>);

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>{isNew ? 'New OAuth Client' : name}</h2>
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Name
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                {isNew ? (
                                    <>
                                        <div className="form-group">
                                            <label className="col-sm-2 control-label">Expiration
                                                {requiredIndicator}
                                            </label>
                                            <div className="col-sm-8">
                                                <select className="form-control"
                                                    value={expirationType}
                                                    onChange={(e) => setExpirationType(e.target.value)}
                                                >
                                                    <option value="6months">6 months</option>
                                                    <option value="12months">12 months</option>
                                                    <option value="custom">Custom</option>
                                                </select>
                                            </div>
                                        </div>

                                        {expirationType === 'custom' && (
                                            <div className="form-group">
                                                <label className="col-sm-2 control-label">Expiration Date
                                                    {requiredIndicator}
                                                </label>
                                                <div className="col-sm-8">
                                                    <PickDate
                                                        value={customDate}
                                                        onChange={(date) => setCustomDate(date)}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    customDate && (
                                        <div className="form-group">
                                            <label className="col-sm-2 control-label">Expiration</label>
                                            <div className="col-sm-8">
                                                <input className="form-control" type="text"
                                                    value={dateFormat(customDate, 'dd-mm-yyyy')}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    )
                                )}

                                {!isNew && (
                                        <div className="form-group">
                                            <label className="col-sm-2 control-label">Client ID</label>
                                            <div className="col-sm-8">
                                                <input className="form-control" type="text"
                                                    value={clientId}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                )}

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Redirect URIs</label>
                                    <div className="col-sm-8">
                                        {redirectUris.map((uri, index) => (
                                            <div className="input-group" key={index} style={{marginBottom: '5px'}}>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    value={uri}
                                                    onChange={(e) => updateRedirectUri(index, e.target.value)}
                                                    placeholder="https://example.com/callback"
                                                />
                                                <span className="input-group-btn">
                                                    <button
                                                        type="button"
                                                        className="btn btn-white"
                                                                onClick={() => removeRedirectUri(index)}
                                                            >
                                                                <i className="fa fa-minus"></i>
                                                            </button>
                                                        </span>
                                            </div>
                                        ))}
                                        <button type="button" className="btn btn-white btn-sm" onClick={addRedirectUri}>
                                            <i className="fa fa-plus"></i> Add Redirect URI
                                        </button>
                                    </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage error={error} />
                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />
                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={true}
                                            spinnerActive={submitActive}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />
                                        <DeleteButton
                                           isHidden={isNew}
                                           url={objectUrl}
                                           onError={handleSubmitError}
                                        />
                                    </div>
                                </div>
                            </form>

                            {showSecretModal && (
                                <>
                                    <div className="modal inmodal show" style={{display: 'block'}}>
                                        <div className="modal-dialog">
                                            <div className="modal-content">
                                                <div className="modal-header">
                                                    <h4 className="modal-title">OAuth Client Created</h4>
                                                </div>
                                                <div className="modal-body">
                                                    <p>Your client has been created. Please copy the credentials now — the client secret will not be shown again.</p>
                                                    <div className="form-group">
                                                        <label>Client ID</label>
                                                        <input className="form-control" type="text" value={newClientId} readOnly onFocus={(e) => e.target.select()} />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Client Secret</label>
                                                        <input className="form-control" type="text" value={newClientSecret} readOnly onFocus={(e) => e.target.select()} />
                                                    </div>
                                                </div>
                                                <div className="modal-footer">
                                                    <button type="button" className="btn btn-primary" onClick={handleCloseSecretModal}>
                                                        Done
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-backdrop in show"></div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default OAuthClient
